"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import { ArrowRight, Globe2 } from "lucide-react";
import { useBookCallPopup } from "@/components/booking/CcBookCallPopupProvider";
import { bookCallOriginFromElement } from "@/lib/integrations/book-call-popup-origin";
import { normalizeWebsite } from "../seo-website-input";
import type { SeoAnalysisCopy } from "./seo-specialist-copy";
import styles from "./seo-specialist.module.scss";

export function CcSeoReviewRequest({
  copy,
}: {
  copy: SeoAnalysisCopy["closing"]["request"];
}) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const booking = useBookCallPopup();
  const [website, setWebsite] = useState("");
  const [error, setError] = useState(false);
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = normalizeWebsite(website);
    if (!normalized) {
      setError(true);
      return;
    }
    setError(false);
    booking.open(
      button.current ? bookCallOriginFromElement(button.current) : undefined,
      { variant: "seo", prefill: { website: normalized } },
    );
  }
  return (
    <form data-analytics-form="seo_audit_prefill" className={styles.request} noValidate onSubmit={submit}>
      <label htmlFor={`${id}-website`} className={styles.srOnly}>
        {copy.label}
      </label>
      <div className={styles.requestRow}>
        <Globe2 size={20} aria-hidden="true" />
        <input
          id={`${id}-website`}
          type="text"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder={copy.placeholder}
          value={website}
          onChange={(e) => {
            setWebsite(e.target.value);
            setError(false);
          }}
          aria-invalid={error}
          aria-describedby={`${id}-note${error ? ` ${id}-error` : ""}`}
        />
        <button ref={button} type="submit">
          {copy.button}
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
      {error && (
        <p className={styles.formError} id={`${id}-error`} role="alert">
          {copy.error}
        </p>
      )}
      <p className={styles.requestNote} id={`${id}-note`}>
        {copy.note}
      </p>
    </form>
  );
}
